import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, type SubmitErrorHandler, useForm } from "react-hook-form";
import { z } from "zod";

import {
  Button,
  classNames,
  Input,
  Modal,
  Text,
  toastError,
  toastSuccess,
} from "@alpacahq/ui/old";

import {
  getErrorMessage,
  useGetTradingAccount,
  useMutationRequest,
} from "~/v2/api/hooks";
import type { Owner } from "~/v2/api/rest/account";
import { patchOwner, patchOwnerType } from "~/v2/api/rest/entityOnboarding";
import { logAmplitudeEvent } from "../../util";
import {
  type UBOQuickAdd,
  type UltimateBeneficialOwner,
  zUBOQuickAdd,
} from "./schema";

type UBOQuickAddFormProps = {
  owner: Owner;
  onClose: () => void;
  isPatch: boolean;
  percentageOwnershipAvailable?: number;
  isOpen: boolean;
};

export const UBOQuickAddForm = ({
  owner,
  onClose,
  isPatch,
  percentageOwnershipAvailable = 100,
  isOpen,
}: UBOQuickAddFormProps) => {
  const { account } = useGetTradingAccount();

  const form = useForm<UBOQuickAdd>({
    mode: "onChange",
    resolver: zodResolver(
      zUBOQuickAdd.superRefine(
        ({ percentage_ownership }, refinementContext) => {
          if (percentage_ownership > percentageOwnershipAvailable) {
            refinementContext.addIssue({
              code: z.ZodIssueCode.custom,
              message: `The sum of each Ultimate Beneficial Owners' ownership percentage cannot exceed 100%. There is ${percentageOwnershipAvailable}% available.`,
              path: ["percentage_ownership"],
            });
          }
        },
      ),
    ),
    defaultValues: { percentage_ownership: owner?.percentage_ownership ?? 0 },
  });

  const addAsUBOMutation = useMutationRequest(
    async (data: UBOQuickAdd) => {
      await patchOwnerType({
        accountId: account?.id ?? "",
        ownerId: owner.owner_id,
        isAuthorizedIndividual: true,
        isUBO: true,
      });

      return patchOwner({
        type: "ubo",
        accountId: account?.id ?? "",
        ownerId: owner.owner_id,
        data,
      });
    },
    {
      onSuccess: () => {
        toastSuccess(`UBO ${isPatch ? "edited" : "created"}`);
        onClose();
      },
      onError: (error) => {
        const errorMessage = getErrorMessage(error);
        toastError(
          `Error ${isPatch ? "editing" : "creating"} UBO${
            isPatch ? "" : " from Authorized Individual"
          }: ${errorMessage}`,
        );
      },
    },
  );

  const removeAsUBOMutation = useMutationRequest(
    async () =>
      patchOwnerType({
        accountId: account?.id ?? "",
        ownerId: owner.owner_id,
        isAuthorizedIndividual: true,
        isUBO: false,
      }),
    {
      onSuccess: () => {
        toastSuccess("UBO removed");
        onClose();
      },
      onError: (error) => {
        const errorMessage = getErrorMessage(error);
        toastError(`Error removing UBO: ${errorMessage}`);
      },
    },
  );

  const onSubmitError: SubmitErrorHandler<UltimateBeneficialOwner> = (errs) => {
    toastError("Form has errors. Please check the form and try again.");
    const errorMessage = Object.values(errs)
      .map((e) => e.message)
      .join(", ");
    logAmplitudeEvent.invalidInput(errorMessage);
  };

  const onSubmit = (values: UBOQuickAdd) => addAsUBOMutation.mutate(values);

  const isSubmitDisabled =
    addAsUBOMutation.isPending || removeAsUBOMutation.isPending;

  return (
    <Modal
      title={"Add Ownership Percentage"}
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      disableOutsideClick
    >
      <div>
        <form
          onSubmit={form.handleSubmit(onSubmit, onSubmitError)}
          className="flex flex-col gap-3"
        >
          <Text>
            Enter percentage ownership of {owner.given_name} {owner.family_name}
            . Only include Ultimate Beneficial Owners who's ownership is 10% or
            greater.
          </Text>

          <Controller
            control={form.control}
            name="percentage_ownership"
            render={({ field, fieldState: { error } }) => (
              <>
                <Input
                  {...field}
                  type="number"
                  placeholder="Enter Ownership Percentage"
                  rightIcon={<span>%</span>}
                  className="bg-white"
                />
                {error?.message && (
                  <Text className="text-rose-500 text-xs">{error.message}</Text>
                )}
              </>
            )}
          />
        </form>

        <div
          className={classNames(
            "flex w-full items-center gap-4 pt-9",
            isPatch ? "justify-between" : "justify-end",
          )}
        >
          {isPatch && (
            <Button
              type="button"
              variant="red"
              disabled={isSubmitDisabled}
              onClick={() => removeAsUBOMutation.mutate(true)}
            >
              Remove
            </Button>
          )}
          <Button
            type="submit"
            onClick={form.handleSubmit(onSubmit, onSubmitError)}
            disabled={isSubmitDisabled}
          >
            Submit
          </Button>
        </div>
      </div>
    </Modal>
  );
};
